import React from 'react';

interface PaginationInfo {
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  totalItems: number;
  startItem: number;
  endItem: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
}

interface PaginationControlsProps {
  /** Informazioni sulla paginazione */
  paginationInfo: PaginationInfo;
  /** Funzione per andare a una pagina specifica */
  onGoToPage: (page: number) => void;
  /** Funzione per andare alla prima pagina */
  onGoToFirstPage: () => void;
  /** Funzione per andare alla pagina precedente */
  onGoToPreviousPage: () => void;
  /** Funzione per andare alla pagina successiva */
  onGoToNextPage: () => void;
  /** Funzione per andare all'ultima pagina */
  onGoToLastPage: () => void;
  /** Funzione per cambiare gli elementi per pagina */
  onChangeItemsPerPage: (itemsPerPage: number) => void;
  /** Opzioni disponibili per elementi per pagina */
  itemsPerPageOptions?: number[];
  /** Mostra i controlli superiori (default: true) */
  showTopControls?: boolean;
  /** Mostra i controlli inferiori (default: true) */
  showBottomControls?: boolean;
  /** Numero massimo di bottoni pagina da mostrare (default: 5) */
  maxPageButtons?: number;
}

/**
 * Componente per i controlli di paginazione
 * Fornisce controlli standardizzati per navigare tra le pagine
 */
export const PaginationControls: React.FC<PaginationControlsProps> = ({
  paginationInfo,
  onGoToPage,
  onGoToFirstPage,
  onGoToPreviousPage,
  onGoToNextPage,
  onGoToLastPage,
  onChangeItemsPerPage,
  itemsPerPageOptions = [5, 10, 25, 50, 100],
  showTopControls = true,
  showBottomControls = true,
  maxPageButtons = 5
}) => {
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    startItem,
    endItem,
    canGoPrevious,
    canGoNext
  } = paginationInfo;

  // Genera array di numeri di pagina da visualizzare
  const getPageNumbers = (): number[] => {
    if (totalPages <= maxPageButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const halfButtons = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfButtons);
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // Aggiusta se siamo vicini alla fine
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    return Array.from(
      { length: endPage - startPage + 1 }, 
      (_, i) => startPage + i
    );
  };

  const pageNumbers = getPageNumbers();

  // Controlli superiori
  const TopControls = () => (
    <div className="pagination-controls top">
      <div className="items-per-page">
        <label htmlFor="itemsPerPage">Elementi per pagina:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => onChangeItemsPerPage(parseInt(e.target.value))}
        >
          {itemsPerPageOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      
      <div className="pagination-info">
        Mostrando {startItem}-{endItem} di {totalItems} risultati
      </div>
    </div>
  );

  // Controlli inferiori
  const BottomControls = () => (
    <div className="pagination-controls bottom">
      <div className="pagination-info">
        Pagina {currentPage} di {totalPages}
      </div>
      
      <div className="pagination-buttons">
        <button 
          className="btn-pagination"
          onClick={onGoToFirstPage}
          disabled={!canGoPrevious}
          title="Prima pagina"
          aria-label="Vai alla prima pagina"
        >
          «
        </button>
        
        <button 
          className="btn-pagination"
          onClick={onGoToPreviousPage}
          disabled={!canGoPrevious}
          title="Pagina precedente"
          aria-label="Vai alla pagina precedente"
        >
          ‹
        </button>
        
        {/* Paginazione numerica */}
        {pageNumbers.map(pageNum => (
          <button
            key={pageNum}
            className={`btn-pagination ${currentPage === pageNum ? 'active' : ''}`}
            onClick={() => onGoToPage(pageNum)}
            aria-label={`Vai alla pagina ${pageNum}`}
            aria-current={currentPage === pageNum ? 'page' : undefined}
          >
            {pageNum}
          </button>
        ))}
        
        <button 
          className="btn-pagination"
          onClick={onGoToNextPage}
          disabled={!canGoNext}
          title="Pagina successiva"
          aria-label="Vai alla pagina successiva"
        >
          ›
        </button>
        
        <button 
          className="btn-pagination"
          onClick={onGoToLastPage}
          disabled={!canGoNext}
          title="Ultima pagina"
          aria-label="Vai all'ultima pagina"
        >
          »
        </button>
      </div>
    </div>
  );

  // Se non ci sono abbastanza dati per la paginazione, non mostrare nulla
  if (totalItems === 0) {
    return null;
  }

  return (
    <>
      {showTopControls && <TopControls />}
      {showBottomControls && totalPages > 1 && <BottomControls />}
    </>
  );
};

export default PaginationControls;